<script setup lang="ts">
import { Card, EmptyState, ErrorState, Skeleton } from '@taler/ui'
import { useI18n } from 'vue-i18n'
import InboxItem from '@/features/transfers/components/InboxItem.vue'
import type { TransferView } from '../internal/transfer-view.ts'

defineProps<{
  items: readonly TransferView[]
  isLoading: boolean
  error: Error | null
  retrying?: boolean
  ledgerName: (ledgerId: string) => string
  isBusy: (transferId: string) => boolean
}>()

const emit = defineEmits<{
  accept: [transfer: TransferView, targetAccountId: string]
  reject: [transfer: TransferView]
  retry: []
}>()

const { t } = useI18n()
</script>

<template>
  <div>
    <ul v-if="isLoading" aria-hidden="true" class="flex flex-col gap-4">
      <li
        v-for="index in 2"
        :key="index"
        class="flex flex-col gap-3 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-6 bg-[color:var(--color-surface)]"
      >
        <Skeleton width="40%" height="1rem" />
        <Skeleton width="60%" height="1.5rem" />
        <Skeleton width="30%" height="1rem" />
      </li>
    </ul>
    <p v-if="isLoading" class="sr-only" role="status">{{ t('transfers.inboxLoading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('transfers.inboxErrorTitle')"
      :body="t('transfers.inboxErrorBody')"
      :retry-label="t('transfers.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState
      v-else-if="items.length === 0"
      :title="t('transfers.inboxEmptyTitle')"
      :body="t('transfers.inboxEmptyBody')"
    >
      <template #action>
        <div class="flex flex-col items-center gap-[var(--space-tight)]">
          <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
            {{ t('transfers.inboxEmptyRelationshipHint') }}
          </p>
          <RouterLink :to="{ name: 'relationships' }" class="text-[color:var(--color-accent)] underline">
            {{ t('transfers.inboxEmptyRelationshipLink') }}
          </RouterLink>
        </div>
      </template>
    </EmptyState>

    <Card v-else padding="none">
      <ul class="flex flex-col divide-y divide-[color:var(--color-border)]">
        <InboxItem
          v-for="transfer in items"
          :key="transfer.id"
          :transfer="transfer"
          :source-ledger-name="ledgerName(transfer.sourceLedgerId)"
          :is-busy="isBusy(transfer.id)"
          @accept="(targetAccountId) => emit('accept', transfer, targetAccountId)"
          @reject="emit('reject', transfer)"
        />
      </ul>
    </Card>
  </div>
</template>
